import { Button, Content, Div, Flex, Heading, OverlineText, Stack } from "@hopper-ui/components";
import { DismissIcon, OrganizationIcon } from "@hopper-ui/icons";
import { InitializingCategoryKeys, MigrateCategoryKeys } from "../../api/crawlCategories.ts";
import { type TenantCrawlOverviewDto } from "../../api/types.ts";
import { GridCardList } from "@sg-protect/components";
import { useContext } from "react";
import { OverlayTriggerStateContext, RouterProvider } from "react-aria-components";
import { type NavigateOptions, useNavigate } from "react-router";
import { ScanCard } from "./ScanCard.tsx";
import { BulkOperationCard, type BulkOperationCardProps } from "./BulkOperationCard.tsx";
import type { FailedOperation } from "../remediation/BulkProvider.tsx";
import { FailedOperationCard } from "./FailedOperationCard.tsx";
import { ViewActivityLogLink } from "./ViewActivityLogLink.tsx";
import { getTenantName } from "../../utils/entityHelpers.ts";
import styles from "./ActivityPanel.module.css";

export interface ActivityPanelProps {
    primaryTenantName?: string;
    primaryTenant?: TenantCrawlOverviewDto;
    migrateTenants: TenantCrawlOverviewDto[];
    operationCards: BulkOperationCardProps[];
    failedOperations: FailedOperation[];
}

const BODY_MAX_HEIGHT = "31.5rem";

interface TenantLabelProps {
    name: string;
}

function TenantLabel({ name }: TenantLabelProps) {
    return (
        <Flex gap="inline-sm" alignItems="center">
            <OrganizationIcon size="sm" />
            <OverlineText color="neutral-weak">{name}</OverlineText>
        </Flex>
    );
}

export function ActivityPanel({
    primaryTenantName,
    primaryTenant,
    migrateTenants,
    operationCards,
    failedOperations
}: ActivityPanelProps) {
    const navigate = useNavigate();
    const overlayState = useContext(OverlayTriggerStateContext);
    const hasOperations = failedOperations.length > 0 || operationCards.length > 0;
    const hasProtectActivity = primaryTenant !== undefined || hasOperations;

    // The panel lives in the header, so in-app navigation leaves its popover open unless we close it.
    // react-aria only calls this for same-tab navigation, so a modified click (new tab) keeps it open.
    const closeAndNavigate = (path: string, options?: NavigateOptions) => {
        overlayState?.close();
        void navigate(path, options);
    };

    return (
        <RouterProvider navigate={closeAndNavigate}>
            <Content UNSAFE_width="25rem" marginTop="core_0" display="flex" flexDirection="column" minHeight="core_0">
                <Flex alignItems="start" justifyContent="space-between" marginBottom="stack-sm">
                    <Heading size="xs" data-public>
                        Activity
                    </Heading>
                    <Button slot="close" variant="ghost-secondary" size="sm" aria-label="Dismiss" marginTop="core_0">
                        <DismissIcon />
                    </Button>
                </Flex>
                <Div
                    overflowY="auto"
                    minHeight="core_0"
                    UNSAFE_maxHeight={BODY_MAX_HEIGHT}
                    paddingTop="core_20"
                    paddingBottom="inset-md"
                    paddingLeft="core_20"
                    paddingRight="inset-sm"
                    className={styles.body}
                >
                    <Stack gap="stack-md">
                        {migrateTenants.map(tenant => (
                            <Stack gap="stack-sm" key={tenant.tenantId}>
                                <TenantLabel name={getTenantName(tenant)} />
                                <ScanCard tenant={tenant} categoryKeys={MigrateCategoryKeys} />
                            </Stack>
                        ))}
                        {hasProtectActivity && (
                            <Stack gap="stack-sm">
                                {primaryTenantName && <TenantLabel name={primaryTenantName} />}
                                {primaryTenant && (
                                    <ScanCard tenant={primaryTenant} categoryKeys={InitializingCategoryKeys} />
                                )}
                                {hasOperations && (
                                    <GridCardList aria-label="Operations" gridTemplateColumns="1fr" gap="stack-sm">
                                        {failedOperations.map(operation => (
                                            <FailedOperationCard key={operation.bulkId} operation={operation} />
                                        ))}
                                        {operationCards.map(card => (
                                            <BulkOperationCard key={card.operation.bulkId} {...card} />
                                        ))}
                                    </GridCardList>
                                )}
                            </Stack>
                        )}
                    </Stack>
                </Div>
                <ViewActivityLogLink />
            </Content>
        </RouterProvider>
    );
}
